import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { Link } from 'react-router-dom'
import { useFormContext, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { DateRange, OpenInNew as OpenInNewIcon } from '@mui/icons-material_modern'
import { Box, Typography, Stack, useTheme, Button, Tooltip, TextField, Autocomplete } from '@mui/material'
import Input from 'components/generic/form/Input'
import DatePicker from 'components/generic/form/DatePicker'
import TimePicker from 'components/generic/form/TimePicker'
import DateTimePicker from 'components/generic/form/DateTimePicker'
import Select from 'components/generic/form/Select'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import Checkbox from 'components/generic/form/Checkbox'
import Switch from 'components/generic/form/Switch'
import SkeletonWrapper from 'components/generic/SkeletonWrapper'

const SubFormLayout = ({ formName, boxStyles, fields, isLoading }) => {
  const theme = useTheme()
  const methods = useFormContext()
  const { defaultYardTimezone } = useSelector(state => state.auth)

  const renderSingleField = settings => {
    const { sx, InputLabelProps } = settings
    const standardFieldSettings = {
      ...(sx && { sx }),
      ...(InputLabelProps && { InputLabelProps }),
      key: settings?.name,
      name: settings?.name,
      label: settings?.label,
      size: settings?.size || 'small',
      variant: settings?.variant || 'outlined',
      'data-test': settings?.dataTest,
      control: methods.control,
      error: !!get(methods?.formState?.errors, settings?.name),
      helperText: get(methods.formState.errors, `${settings?.name}.message`),
      defaultValue: settings?.defaultValue || (settings?.fieldType === 'customerAutocomplete' ? null : ''),
      onBlur: settings?.onBlur,
      onChange: settings?.onChange,
      disabled: !!settings?.disabled,
    }

    const field = (() => {
      switch (settings?.fieldType) {
        case 'customerAutocomplete':
          if (settings?.tooltip) {
            return (
              <Tooltip title={settings?.tooltip} placement={settings?.placement} enterDelay={500}>
                <div style={{ width: '100%' }}>
                  <CustomerAutocomplete
                    {...standardFieldSettings}
                    freeSolo={false}
                    labelKey={settings?.labelKey}
                    onChange={settings?.onChange}
                    baseFilter={settings?.baseFilter}
                    quickAdd={settings?.quickAdd}
                    autocompleteProps={{
                      renderOption: (props, option) => (
                        <li {...props} key={option.id}>
                          {option.displayName}
                        </li>
                      ),
                      ...settings?.autocompleteProps,
                    }}
                    intercompany={settings?.intercompany}
                  />
                </div>
              </Tooltip>
            )
          } else {
            return (
              <CustomerAutocomplete
                {...standardFieldSettings}
                freeSolo={false}
                labelKey={settings?.labelKey}
                onChange={settings?.onChange}
                baseFilter={settings?.baseFilter}
                quickAdd={settings?.quickAdd}
                autocompleteProps={{
                  renderOption: (props, option) => (
                    <li {...props} key={option.id}>
                      {option.displayName}
                    </li>
                  ),
                  ...settings?.autocompleteProps,
                }}
                intercompany={settings?.intercompany}
              />
            )
          }
        case 'input':
          return (
            <Input
              {...standardFieldSettings}
              {...(settings?.InputProps && { InputProps: settings?.InputProps })}
              multiline={settings?.multiline}
              minRows={settings?.minRows}
              maxRows={settings?.maxRows}
            />
          )
        case 'switch':
          return (
            <Switch
              {...standardFieldSettings}
              label={settings?.label}
              name={settings?.name}
              control={methods.control}
              defaultValue={settings?.defaultValue}
              disabled={settings?.disabled}
              onChange={settings?.onChange}
              onBlur={settings?.onBlur}
              labelPlacement={settings?.labelPlacement}
            />
          )
        case 'inputTooltip':
          return (
            <Tooltip title={settings?.tooltip} placement={settings?.placement} enterDelay={500}>
              <div style={{ width: '100%' }}>
                <Input
                  {...standardFieldSettings}
                  InputProps={{
                    ...settings?.InputProps,
                  }}
                />
              </div>
            </Tooltip>
          )
        case 'date': {
          const { defaultValue, ...rest } = standardFieldSettings
          return (
            <DatePicker
              defaultDate={settings.defaultDate}
              datePickerProps={{
                components: {
                  OpenPickerIcon: DateRange,
                },
                ...(settings?.datePickerProps || {}),
              }}
              {...rest}
            />
          )
        }
        case 'time': {
          const { defaultValue, ...rest } = standardFieldSettings
          return <TimePicker defaultTime={settings.defaultTime} timePickerProps={settings.timePickerProps} {...rest} />
        }
        case 'dateTime': {
          const { defaultValue, ...rest } = standardFieldSettings
          return (
            <DateTimePicker
              defaultDate={settings.defaultDate}
              dateTimePickerProps={{
                size: 'small',
                timezone: defaultYardTimezone,
                OpenPickerIcon: DateRange,
              }}
              {...rest}
            />
          )
        }
        case 'dropdown':
          return (
            <Select
              {...standardFieldSettings}
              selectProps={{ ...settings?.selectProps }}
              items={settings?.dropdownOptions}
            />
          )
        case 'autocomplete': {
          const {
            onChange: _omitted,
            error: _error,
            helperText: _helperText,
            ...restFieldSettings
          } = standardFieldSettings
          return (
            <Controller
              name={settings.name}
              control={methods.control}
              defaultValue={settings.defaultValue}
              render={({ field: { onChange: controllerOnChange, value } }) => (
                <Autocomplete
                  {...restFieldSettings}
                  options={settings?.options}
                  getOptionLabel={settings?.getOptionLabel}
                  value={value ? settings?.options?.find(opt => opt.id === value) || null : null}
                  onChange={(_, newValue) => {
                    const updatedValue = newValue ? { ...newValue } : null
                    controllerOnChange(updatedValue?.id || null)
                    settings?.onChange?.(_, updatedValue)
                  }}
                  disableClearable={settings?.disableClearable}
                  sx={{ flex: 1 }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={settings?.label}
                      variant={settings?.variant}
                      size={settings?.size}
                      error={!!get(methods?.formState?.errors, settings?.name)}
                      helperText={get(methods.formState.errors, `${settings?.name}.message`)}
                      data-test={settings?.dataTest}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          )
        }
        case 'static':
          return (
            <Input
              {...standardFieldSettings}
              variant="standard"
              disabled
              sx={{
                'input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'textarea.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'label.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.secondary,
                },
              }}
              InputProps={{
                disableUnderline: true,
                multiline: settings?.InputProps?.multiline,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )
        case 'button':
          return (
            <Button
              {...standardFieldSettings}
              fullWidth
              sx={{ padding: 0, maxHeight: '40px', ...settings?.sx }}
              onClick={() => settings?.onClick()}
              color={settings?.sideButton?.color}
            >
              {settings?.label}
            </Button>
          )
        case 'link':
          return (
            <Link to={`${settings?.link}`} target="_blank" style={{ textDecoration: 'none' }}>
              <Button startIcon={<OpenInNewIcon />}>View</Button>
            </Link>
          )
        case 'checkbox':
          return (
            <Checkbox
              name={settings?.name}
              label={settings?.label}
              defaultValue={settings?.defaultValue}
              disabled={settings?.disabled}
              control={methods.control}
              tooltipText={settings?.tooltipText}
              onChange={settings?.onChange}
            />
          )
        case 'blank':
          return <Stack flex={1} />
        case 'custom': {
          const CustomComponent = settings?.customComponent
          return <CustomComponent {...standardFieldSettings} />
        }

        default:
          return null
      }
    })()

    const formattedField = isLoading ? <SkeletonWrapper isLoading={isLoading}>{field}</SkeletonWrapper> : field

    if (settings?.sideButton) {
      return (
        <Stack direction="row" sx={{ width: '100%', ...standardFieldSettings?.sx }}>
          {formattedField}
          {isLoading ? null : (
            <Button
              {...settings?.sideButton}
              sx={{
                minWidth: 'unset',
                width: 40,
                paddingRight: 0,
                marginRight: -1,
                ...settings?.sideButton?.sx,
              }}
            />
          )}
        </Stack>
      )
    }
    return formattedField
  }

  const renderRows = row => {
    const visibleFields = row.filter(field => !field.hidden)
    if (visibleFields.length === 0) return null
    return (
      <Stack direction="row" key={`${row?.[0]?.name}`} spacing={2}>
        {visibleFields.map(field => (
          <Fragment key={field?.name}>{renderSingleField(field)}</Fragment>
        ))}
      </Stack>
    )
  }

  return (
    <>
      <Box width={{ xs: '100%', md: '40%' }} sx={boxStyles}>
        <Typography variant="h6" gutterBottom color="textSecondary">
          {formName}
        </Typography>
        <Stack
          padding={2}
          spacing={2}
          sx={{
            backgroundColor: 'background.section',
            borderRadius: '8px',
          }}
        >
          {fields.map(renderRows)}
        </Stack>
      </Box>
    </>
  )
}

SubFormLayout.propTypes = {
  formName: PropTypes.string.isRequired,
  boxStyles: PropTypes.object,
  fields: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        dataTest: PropTypes.string,
        fieldType: PropTypes.oneOf([
          'customerAutocomplete',
          'input',
          'inputTooltip',
          'date',
          'time',
          'datetime',
          'dropdown',
          'autocomplete',
          'static',
          'button',
          'link',
          'checkbox',
          'blank',
          'custom',
        ]),
      }),
    ),
  ).isRequired,
  isLoading: PropTypes.bool,
}

SubFormLayout.defaultProps = {
  boxStyles: {},
  isLoading: false,
}

export default SubFormLayout
